import React, {type FC, useEffect, useMemo, useState} from 'react';

import {LoadingDots} from '../../../../../LoadingDots';
import {useWFHooks} from '../../pages/wfReactInterface/context';
import {MiniAudioViewer} from '../Content/AudioView';
import {type CustomWeaveTypePayload} from '../customWeaveType.types';

type AudioPlayerTypePayload = CustomWeaveTypePayload<
  'openai._legacy_response.HttpxBinaryResponseContent',
  Record<string, string>
>;

export const AudioPlayer: FC<{
  entity: string;
  project: string;
  data: AudioPlayerTypePayload;
}> = ({entity, project, data}) => {
  const {useFileContent} = useWFHooks();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioFileName, setAudioFileName] = useState<string | null>(null);

  // Find the first audio file in the files object
  const audioFile = Object.keys(data.files).find(fileName =>
    fileName.startsWith('audio.')
  );

  // Memoize the params to prevent unnecessary re-queries
  const fileContentParams = useMemo(
    () => ({
      entity,
      project,
      digest: audioFile ? data.files[audioFile] : '',
    }),
    [entity, project, audioFile, data.files]
  );

  const audioBinary = useFileContent(fileContentParams);

  useEffect(() => {
    if (audioFile) {
      setAudioFileName(audioFile);
    }
  }, [audioFile]);

  useEffect(() => {
    if (audioBinary.result) {
      setAudioUrl(URL.createObjectURL(new Blob([audioBinary.result])));
    }
  }, [audioBinary.result]);

  if (audioBinary.loading) {
    return <LoadingDots />;
  } else if (audioBinary.result == null || audioUrl == null) {
    return <span></span>;
  }

  const downloadFile = () => {
    if (!audioUrl) {
      console.error('Audio URL is not available');
      return;
    }
    const a = document.createElement('a');
    a.href = audioUrl;
    const date = new Date().toISOString().split('T')[0];
    a.download = `${project}_${date}_${entity}_${audioFileName}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <MiniAudioViewer
      audioSrc={audioUrl}
      height={24}
      downloadFile={downloadFile}
    />
  );
};
